<template>
    <div class="public-road-access-app">
        <loading 
            :active="loading_pra || saving_pra"
            :can-cancel="false" 
            :is-full-page="false"
        />

        <div v-if="!pra" class="panel panel-default text-center">
            <div class="panel-heading">
                {{ $t("analysis:tabs:public_road_access") }}
            </div>
            <div class="panel-wrapper collapse in">
                <div class="panel-body">
                    <div class="row" v-if="failed">
                        <p>{{ $t("amas:analysis:pra:access_analysis_failed") }}</p>
                        <button type="button" class="btn btn-info btn-outline" v-on:click="backendRecreate()">
                            {{ $t("general:re_collect") }}
                        </button>
                    </div>

                    <div class="row" v-else>
                        <p>{{ $t("amas:analysis:pra:getting_access") }}</p>
                        <i class="fa fa-spinner fa-spin"></i>
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="panel panel-default">
            
            <div class="panel-heading">
                {{ $t("analysis:tabs:public_road_access") }}

                <div class="pull-right">

                    <button type="button" class="btn btn-success btn-sm btn-outline m-r-5"
                        v-tooltip="$t('general:save')"
                        v-on:click.prevent="savePra()"
                    >
                        {{ $t("general:save") }}
                    </button>

                    <button type="button" class="btn btn-info btn-sm btn-outline"
                        v-on:click="backendRecreate()"
                    >
                        {{ $t("amas:analysis:pra:automatically_update") }}
                    </button>
                    
                </div>
            </div>

            <div class="panel-wrapper collapse in">
                <div class="panel-body">
                    <div class="row">
                        <div class="col-md-6">
                            <div class="has-access-selector-container">
                                <span class="m-r-10">{{ $t("amas:analysis:pra:legal_access") }}</span>
                                <true-false-selector
                                    v-if="pra.result"
                                    v-model="pra.result.has_access"
                                />
                            </div>

                            <textarea
                                v-if="pra.result"
                                v-model="pra.result.comment" placeholder="Komentar"
                                class="form-control" rows="5" style="resize: none;"
                            />

                            <div class="m-t-10">
                                <add-rekeys-textarea
                                    @submit="addStep"
                                    :rows="1"
                                />
                            </div>
                        </div>

                        <div class="col-md-6">
                            <loading 
                                :active="loading_polygons"
                                :can-cancel="false" 
                                :is-full-page="false"
                            />

                            <k-wms-leaflet-map
                                :polygons="roadAccessPolygons"
                                :center="center"
                                :legend="legend"
                                @polygon-mouseover="highlightItem($event)"
                                @polygon-mouseout="unhighlightItem($event)"
                                @click="handleMapClick"
                                :disable-auto-fit="disableAutoFit"
                                :fullscreen="true"
                                :zoom="18"
                                :max-zoom="20"
                                :height="250"
                            >

                                <template v-slot:markers>
                                    <l-marker
                                        v-if="clicked_marker"
                                        :lat-lng="clicked_marker.gps_point.coordinates"
                                        :icon="blankMarkerIcon"
                                        ref="clickmarker"
                                    >
                                        <l-popup>
                                            <pra-popup-table
                                                :step="clicked_marker.parcel && getStepByPropertyID(clicked_marker.parcel.property_id)"
                                                :real_estate="clicked_marker.parcel && getRealEstate(clicked_marker.parcel.property_id)"
                                                :parcel="clicked_marker.parcel"
                                                :property_id="clicked_marker.parcel && clicked_marker.parcel.property_id"
                                                :can-add="clicked_marker.parcel && canAdd(clicked_marker.parcel.property_id)"
                                                :can-remove="clicked_marker.parcel && canRemove(clicked_marker.parcel.property_id)"
                                                @add_step="addStep"
                                                @remove_step="removeStep"
                                            />
                                        </l-popup>
                                    </l-marker>
                                    <l-polygon
                                        v-if="clickedParcelPolygon"
                                        :lat-lngs="clickedParcelPolygon.coordinates"
                                        :fill-color="clickedParcelPolygon.style.color"
                                        :stroke="clickedParcelPolygon.style.stroke"
                                        :color="clickedParcelPolygon.style.color"
                                        :weight="clickedParcelPolygon.style.weight"
                                        :fill-opacity="clickedParcelPolygon.style.fillOpacity"
                                    />
                                </template>

                                <template v-slot:popup_content_polygon="slotProps">
                                    <pra-popup-table
                                        :step="slotProps.polygon.step"
                                        :real_estate="getRealEstate(slotProps.polygon.property_id)"
                                        :parcel="null"
                                        :property_id="slotProps.polygon.property_id"
                                        :can-add="canAdd(slotProps.polygon.property_id)"
                                        :can-remove="canRemove(slotProps.polygon.property_id)"
                                        @add_step="addStep"
                                        @remove_step="removeStep"
                                    />
                                </template>

                            </k-wms-leaflet-map>

                            <div class="m-t-30 hidden">
                                <div v.if="pra.image">
                                    <p>{{ $t("amas:analysis:pra:static_image_of_access") }}</p>
                                    <img style="max-width:100%" class="image" :src="pra.image">
                                </div>
                                <span>
                                    <a class="btn btn-xs btn-outline btn-info m-t-10" :href="update_image_url">
                                        {{ $t("amas:analysis:pra:update_image") }}
                                    </a>
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-md-12">

                            <table class="table table-responsive table-hover">
                                <thead>
                                    <tr>
                                        <th>{{ $t("general:re_key") }}</th>
                                        <!-- <th>{{ $t("general:real_estate:type") }}</th> -->
                                        <th>{{ $t("amas:analysis:pra:public_road") }}</th>
                                        <th>{{ $t("amas:analysis:pra:legal_access") }}</th>
                                        <th>{{ $t("amas:analysis:pra:physical_access") }}</th>
                                        <th>{{ $t("amas:analysis:pra:owners") }}</th>
                                        <th>{{ $t("amas:analysis:pra:easements") }}</th>
                                    </tr>
                                </thead>

                                <!-- Subject (source) parcels: shown read-only at the top so their
                                     owners are visible right away. Not draggable, no remove. -->
                                <tbody>
                                    <access-table-row
                                        v-for="(step, source_index) in sourceParcels"
                                        :key="'tr-source-' + source_index"
                                        :step="step"
                                        :real_estate="getRealEstate(step.property_id)"
                                        :highlight="highlighted_property_id == step.property_id"
                                        :public_road="step.has_public_road"
                                        :is_source="true"
                                        :source_divider="source_index === sourceParcels.length - 1"
                                        @update_legal="setLegalStatusData(step.property_id, true)"
                                    />
                                </tbody>

                                <draggable v-model="accessPathParcels" tag="tbody">
                                    <access-table-row
                                        v-for="(step, step_index) in accessPathParcels"
                                        :key="'tr-path-' + step_index"
                                        :step="step"
                                        :real_estate="getRealEstate(step.property_id)"
                                        :highlight="highlighted_property_id == step.property_id"
                                        :public_road="step.has_public_road"
                                        @remove_step="removeStep(step.property_id)"
                                        @update_legal="setLegalStatusData(step.property_id, true)"
                                    />
                                </draggable>
                            </table>

                            <div v-if="saving_pra" class="pull-right m-t-10">
                                <p>
                                    <i class="fa fa-spinner fa-spin"></i>{{ $t("general:saving") }}
                                </p>
                            </div>
                            
                        </div>
                    </div>
                </div>
            </div>

        </div>

        <notifications 
            group="warn"
        />
    </div>
</template>

<script>
import L from "leaflet";
import { icon } from "leaflet";
import { LMarker, LPopup, LPolygon } from "vue2-leaflet";

import draggable from "vuedraggable";

import ApiService from "@/components/utils/api.service.js"
import ApiCalls from "@/components/utils/api.calls.js"
import { Swal } from "@/components/utils/swal.alerts.js";
import ReOpenModal from "@/components/widgets/ReOpenModal.vue";
import KWMSLeafletMap from "@/components/k-leaflet-map/KWMSLeafletMap"

import AddRekeysTextarea from "@apps/analysis/project/vue/valuation_subject_wizard/components/AddRekeysTextarea.vue"

import AccessTableRow from "./components/AccessTableRow.vue"
import TrueFalseSelector from "./components/TrueFalseSelector.vue"
import PRAPopupTable from "./components/PRAPopupTable.vue"

export default {
    props: {
        session_id: {
            type: Number,
            required: true
        }
    },
    components: {
        draggable,
        ReOpenModal,
        AccessTableRow,
        TrueFalseSelector,
        AddRekeysTextarea,
        "pra-popup-table": PRAPopupTable,
        "k-wms-leaflet-map": KWMSLeafletMap,
        LMarker, LPopup, LPolygon
    },
    data: function () {
        return {
            pra: null,
            saving_pra: false,
            loading_pra: false,
            failed: false,

            add_re_key: "",            
            highlighted_property_id: null,
            clicked_marker: null,
            
            real_estate_data: {},
            original_source_parcels: [],
            surrounding_polygons_raw: [],
            loading_polygons: false,

            // API urls
            pra_url: "/amas/analysis/analysis/api/" + this.session_id + "/road-access",
            update_image_url: "/amas/analysis/" + this.session_id + "/document/road-access/recreate-image",

            // Polygon style settings
            polygon_source_parcel_style: {
                color: "#00753b",
                weight: 3,
                fillOpacity: 0.8
            },
            polygon_in_access_parcel_physical_access_style: {
                color: "#245ab3",
                weight: 3,
                fillOpacity: 0.8
            },
            polygon_in_access_parcel_legal_access_style: {
                color: "#8ed0f9",
                weight: 3,
                fillOpacity: 0.8
            },
            polygon_target_parcel_style: {
                color: "#fffdaa",
                weight: 3,
                fillOpacity: 0.8
            },
            polygon_surrounding_style: {
                color: "#ffffff",
                weight: 2,
                fillOpacity: 0
            },
            clicked_parcel_polygon_style: {
                color: "#fffdaa",
                weight: 3,
                fillOpacity: 0.5
            },

            isPreviewingParcel: false
            
        }
    },
    methods: {
        highlightItem: function(property_id) {
            this.highlighted_property_id = property_id;
        },
        unhighlightItem: function(property_id) {
            this.highlighted_property_id = null;
        },
        handleMapClick: async function(e) {
            this.clicked_marker = {
                gps_point: {
                    "type": "Point", "coordinates": [e.latlng.lat, e.latlng.lng]
                },
                parcel: null
            };
            this.isPreviewingParcel = true;
            this.$nextTick(() => { 
                if (this.$refs.clickmarker) {
                    this.$refs.clickmarker.mapObject.openPopup(); 
                }
            });

            ApiCalls
                .getParcelsByGps(e.latlng.lat, e.latlng.lng)
                .then(res => this.clicked_marker.parcel = res[0]);
        },
        backendRecreate: function() {
            const app = this;
            Swal({
                title: app.$t("general:are_you_sure"),   
                text: app.$t("amas:analysis:pra:system_will_update_access:alert"),
                showCancelButton: true,   
                confirmButtonColor: "#DD6B55",   
                confirmButtonText: app.$t("amas:analysis:pra:yes_update"),
                cancelButtonText: app.$t("general:no_cancel"),
            }).then(function(val){
                if (val.isConfirmed) {
                    app.getPra(true);
                } else {     
                    Swal({
                        title: app.$t("general:cancelled"),
                        icon: "error",
                        text: "",
                    });
                } 
            });
        },
        getPra: function(force_update=false) {
            const app = this;
            app.loading_pra = true;
            return ApiService
                .get(this.pra_url, { params: { force_update: force_update } })
                .then(response => {
                    app.loading_pra = false;
                    app.pra = response.data;
                    app.original_source_parcels = this.pra.result.source_parcels.map(parcel => parcel.property_id);
                    // Backwards compat: the top-level verdict was renamed
                    // has_legal_access -> has_access. Rows stored before the
                    // migration still carry has_legal_access, so mirror it onto
                    // has_access for the binding.
                    if (app.pra.result && app.pra.result.has_access === undefined) {
                        app.$set(app.pra.result, "has_access", !!app.pra.result.has_legal_access);
                    }
                    // Keep the public-road parcel last so the route reads
                    // subject -> ... -> road, even for results stored before
                    // the backend started ordering road-last.
                    if (app.pra.result && Array.isArray(app.pra.result.access_path)) {
                        app.pra.result.access_path = app.orderAccessPathRoadLast(app.pra.result.access_path);
                    }
                    app.setAllRealEstateData();
                    return response.data;
                })
                .catch(error => {
                    app.loading_pra = false;
                    if(error.response.status===404) {
                        Swal({
                            title: "",
                            text: app.$t("amas:analysis:pra:access_not_yet_obtained:alert"),
                            showCancelButton: true,   
                            confirmButtonColor: "#DD6B55",   
                            confirmButtonText: app.$t("amas:analysis:pra:confirm_obtain"),
                            cancelButtonText: app.$t("general:no_cancel"),
                        }).then(function(val){
                            if (val.isConfirmed) {
                                app.getPra(true);
                            } else {
                                app.failed = true;
                                Swal({
                                    title: app.$t("general:cancelled"),
                                    icon: "error",
                                });
                            } 
                        });
                    } else {
                        app.failed = true;
                        Swal({
                            title: app.$t("general:error"),
                            icon: "error",
                            text: error.response.data.message,
                        });
                        throw error;
                    }
                })
        },
        savePra: function() {
            const app = this;
            app.saving_pra = true;
            return ApiService
                .post(this.pra_url, this.pra)
                .then(response => {
                    app.saving_pra = false;
                    this.pra = response.data;
                })
                .catch(error => {
                    app.saving_pra = false;
                    Swal({
                        title: app.$t("general:error"),
                        icon: "error",
                        text: error.response.data.message,
                    });
                    throw error;
                })
        },
        setAllRealEstateData: function() {
            const app = this;
            app.allPropertyIDs.forEach(function(property_id, index){
                app.setRealEstateData(property_id);
            });
        },
        setRealEstateData: function(property_id) {
            const app = this;
            app.$set(app.real_estate_data, property_id, "loading");
            return ApiCalls
                .getRealEstateData(property_id)
                .then((real_estate) => {
                    if (real_estate) {
                        app.$set(app.real_estate_data, property_id, real_estate);
                        return real_estate;
                    }
                });
        },
        setLegalStatusData: function(property_id, show_error=false){
            const app = this;
            app.$set(app.real_estate_data[property_id], "legal_status", "loading");
            return ApiCalls
                .getLegalStatus(property_id)
                .then(legal_status => {
                    app.$set(app.real_estate_data[property_id], "legal_status", legal_status);
                })
                .catch(error => {
                    app.$set(app.real_estate_data[property_id], "legal_status", "error");
                    if (show_error) {
                        this.$notify({
                            group: 'warn',
                            type: 'warn',
                            title: 'Opozorilo',
                            text: (error.response && error.response.data) ? error.response.data.message : "Napaka pri pridobivanju pravnega stanja",
                        });
                    }
                    throw error;
                })
        },
        getRealEstate: function(property_id) {
            return this.real_estate_data[property_id];
        },
        orderAccessPathRoadLast: function(accessPath) {
            // Stable partition: non-road parcels keep their order, the
            // public-road parcel(s) go last (the route's destination).
            const nonRoad = accessPath.filter(p => !p.has_public_road);
            const road = accessPath.filter(p => p.has_public_road);
            return nonRoad.concat(road);
        },
        // Manage steps
        addStep: function(property_id) {
            if( !this.canAdd(property_id) ) { 
                alert("Ni mogoče dodati");
                return;
            }

            const stepData = this.createStepData(property_id);
            const resultArrayKey = this.getResultArrayKey(property_id);
            this.pra.result[resultArrayKey].push(stepData);

            if (this.clicked_marker && this.clicked_marker.parcel && this.clicked_marker.parcel.property_id === property_id) {
                this.addGeometryToParcelLists(this.clicked_marker.parcel);
            }

            this.setRealEstateData(property_id)
                .then((real_estate) => {
                    if (real_estate) {
                        this.setLegalStatusData(property_id);
                    } else {
                        this.removeStep(property_id);
                    }
                })
        },
        removeStep: function(property_id) {
            if( !this.canRemove(property_id) ) { 
                alert("Ni mogoče odstraniti");
                return;
            }

            const resultArrayKey = this.getResultArrayKey(property_id);
            this.pra.result[resultArrayKey] = this.pra.result[resultArrayKey].filter(parcel => parcel.property_id !== property_id);

            const arrayKey = this.getGeometryArrayKey(property_id);
            this.removeFromGeometryArray(arrayKey, property_id);
        },
        canAdd: function(property_id) {
            return !this.allPropertyIDs.includes(property_id);
        },
        canRemove: function(property_id) {
            return this.allPropertyIDs.includes(property_id)
        },
        getStepByPropertyID: function(property_id) {
            if (!this.allParcelsInPra) {
                return null;
            }
            const items = this.allParcelsInPra.filter(step => step.property_id === property_id);
            return items.length > 0 ? items[0] : null;
        },
        getPolygonStyle: function(property_id) {
            if (this.sourcePropertyIDs.includes(property_id)) {
                return this.polygon_source_parcel_style;
            }
            if (this.isPublicRoad(property_id) ) {
                return this.polygon_target_parcel_style;
            }
            if (this.accessPathPropertyIDs.includes(property_id)) {
                if(this.hasLegalAccess(property_id)) {
                    return this.polygon_in_access_parcel_legal_access_style;
                } else {
                    return this.polygon_in_access_parcel_physical_access_style;
                }
            }
            return this.polygon_surrounding_style;
        },

        checkBooleanProperty: function(property_id, propertyName) {
            return this.allParcelsInPra
                .filter(p => p[propertyName] === true)
                .map(p => p.property_id)
                .includes(property_id);
        },
        isPublicRoad: function(property_id) {
            return this.checkBooleanProperty(property_id, "has_public_road");
        },
        hasLegalAccess: function(property_id) {
            return this.checkBooleanProperty(property_id, "has_legal_access");
        },
        hasPhysicalAccess: function(property_id) {
            return this.checkBooleanProperty(property_id, "has_physical_access");
        },
        isInOriginalParcels: function(property_id) {
            return this.original_source_parcels.includes(property_id)
        },

        getGeometryArrayKey: function(property_id) {
            return this.isInOriginalParcels(property_id) ? "source_parcels_with_geometry" : "access_path_with_geometry";
        },
        getResultArrayKey: function(property_id) {
            return this.isInOriginalParcels(property_id) ? "source_parcels" : "access_path";
        },

        createStepData: function(property_id) {
            return {
                property_id: property_id,
                has_public_road: false,
                has_legal_access: true,
                has_physical_access: true,
            };
        },

        ensureGeometryArrayExists: function(arrayKey) {
            if (!this.pra[arrayKey]) {
                this.$set(this.pra, arrayKey, []);
            }
        },
        addToGeometryArray: function(arrayKey, parcelData) {
            const exists = this.pra[arrayKey].some(p => p.property_id === parcelData.property_id);
            if (!exists) {
                this.pra[arrayKey].push(parcelData);
            }
        },
        removeFromGeometryArray: function(arrayKey, property_id) {
            if (this.pra[arrayKey]) {
                this.pra[arrayKey] = this.pra[arrayKey].filter(parcel => parcel.property_id !== property_id);
            }
        },

        addGeometryToParcelLists: function(parcel) {
            const parcelData = {
                id: parcel.id || 0,
                property_id: parcel.property_id,
                units_list: parcel.units_list || [],
                geom: parcel.geom,
                geom_swapped: parcel.geom_swapped
            }

            const arrayKey = this.getGeometryArrayKey(parcel.property_id);
            this.ensureGeometryArrayExists(arrayKey);
            this.addToGeometryArray(arrayKey, parcelData);
        }
    },
    mounted() {
        this.getPra();
    },
    computed: {
        // 1. Source Parcels
        sourceParcels () {
            if (this.pra && this.pra.result && this.pra.result.source_parcels) {
                return this.pra.result.source_parcels;
            }
            return [];
        },
        sourcePropertyIDs() { return this.sourceParcels.map(i=>i.property_id); },
        // 2. Access path Parcels
        accessPathParcels: {
            get () {
                if (this.pra && this.pra.result && this.pra.result.source_parcels) {
                    return this.pra.result.access_path;
                }
                return [];
            },
            set(newValue) {
                if (this.pra && this.pra.result && this.pra.result.source_parcels) {
                   this.pra.result.access_path = newValue
                }
            }
        },
        accessPathPropertyIDs() { return this.accessPathParcels.map(i=>i.property_id); },
        // All
        allParcelsInPra() {
            return this.sourceParcels.concat(this.accessPathParcels);
        },
        allPropertyIDs() { return this.allParcelsInPra.map(i=>i.property_id); },
        // Map polygons
        roadAccessPolygons() {
            const app = this;
            const roadAccessParcels = [];

            if (this.pra) {
                const createPolygonData = (parcel) => ({
                    ...parcel.geom_swapped,
                    property_id: parcel.property_id,
                    style: app.getPolygonStyle(parcel.property_id),
                    step: app.getStepByPropertyID(parcel.property_id),
                });

                const processGeometryArray = (arrayKey) => {
                    if (this.pra[arrayKey]) {
                        this.pra[arrayKey].forEach(parcel => {
                            if (parcel.geom_swapped) {
                                roadAccessParcels.push(createPolygonData(parcel));
                            }
                        });
                    }
                };
                processGeometryArray("source_parcels_with_geometry");
                processGeometryArray("access_path_with_geometry");
            }
            
            return roadAccessParcels;
        },
        clickedParcelPolygon: function() {
            if(this.clicked_marker && this.clicked_marker.parcel) {
                let parcel = this.clicked_marker.parcel;
                return {
                    ...parcel.geom_swapped,
                    ...{
                        style: this.clicked_parcel_polygon_style,
                        parcel: parcel,
                        property_id: parcel.property_id
                    }
                }
            }
            return null;
        },
        blankMarkerIcon: function() {
            // This is a blank marker icon!
            // It's here so that the parcel details work as intended, don't remove.
            var svg = ""
            var iconUrl = "data:image/svg+xml;base64," + btoa(svg);
            return icon({
                iconUrl: iconUrl,
            })
        },
        // Map center and legend
        center() {
            let center;
            if(this.pra && this.pra.map_center) {
                center = [this.pra.map_center[1], this.pra.map_center[0]]
            }
            return center;
        },
        legend () {
            const app = this;
            var legend = L.control({position: 'bottomleft'});
            legend.onAdd = function (leafletmap) {

                let roadAccessLegendDiv = L.DomUtil.create('div', 'leaflet-legend pra-leaflet-legend');
                roadAccessLegendDiv.setAttribute('hidden', true);

                let colors = [
                    app.polygon_source_parcel_style.color,
                    app.polygon_in_access_parcel_physical_access_style.color,
                    app.polygon_in_access_parcel_legal_access_style.color,
                    app.polygon_target_parcel_style.color,
                ];
                let labels = [
                    app.$t("amas:analysis:pra:parcel_in_question"),
                    "Parcela v dostopu - fizično urejena",
                    "Parcela v dostopu - pravno urejena",
                    app.$t("amas:analysis:pra:parcel_with_public_road"),
                ];

                colors.forEach(function(item, index){
                    roadAccessLegendDiv.innerHTML += `
                    <div class="m-b-5">
                        <svg class="leaflet-icon" width="20" height="20">
                            <rect width="20" height="20" style="fill:${item};"/>
                        </svg>
                        <strong style="color:white;"> - ${labels[index]}</strong>
                    </div>
                    `
                })

                // Toggle legend button
                let toggleButton = L.DomUtil.create('button', 'btn btn-xs')
                toggleButton.innerHTML += app.$t("maps:legend")
                L.DomEvent.addListener(toggleButton, 'click', function () {
                    roadAccessLegendDiv.toggleAttribute('hidden');
                });

                let div = L.DomUtil.create('div', 'm-0');
                div.appendChild(toggleButton);
                div.appendChild(roadAccessLegendDiv);

                return div;
            };

            return legend;

        },
        disableAutoFit: function() {
            return this.isPreviewingParcel;
        }
    },
    watch: {
        "clicked_marker.parcel": function(parcel) {
            if (parcel === undefined && this.$refs.clickmarker) {
                this.$refs.clickmarker.mapObject.closePopup();
                this.isPreviewingParcel = false;
            }
        },
        "pra.result": {
            handler: function() {
                // Force popup to update when PRA data changes
                if (this.$refs.clickmarker && this.clicked_marker && this.clicked_marker.parcel) {
                    this.$nextTick(() => {
                        if (this.$refs.clickmarker.mapObject._popup && this.$refs.clickmarker.mapObject._popup.isOpen()) {
                            this.$refs.clickmarker.mapObject._popup.update();
                        }
                    });
                }
            },
            deep: true
        }
    }
}
</script>

<style>
.pra-leaflet-legend {
    background: rgba(0, 0, 0, 0.5);
    padding: 5px;
}
.pra-leaflet-legend .leaflet-icon {
    vertical-align: middle;
}

.public-road-access-app .has-access-selector-container {
    display: flex;
    align-items: center;
}
.public-road-access-app .dropdown-menu {
    padding: 0;
}

.public-road-access-app  .leaflet-popup-content {
    width: 500px!important;
    margin: 0px;
} .public-road-access-app  .leaflet-popup-content table {
    margin: 0px;
} .public-road-access-app  .leaflet-popup-content table th {
    padding: 3px;
    text-align: center;
    font-weight: bold;
} .public-road-access-app  .leaflet-popup-content table td {
    padding: 3px;
    text-align: center;
    vertical-align: middle;
} .public-road-access-app  .leaflet-popup-content select.form-control {
    padding: 3px;
    height: 20px;
    font-size: 10px;
}
</style>